import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { format, subMonths, startOfMonth, endOfMonth } from 'date-fns';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { SubmissionStatusBadge } from '../components/StatusBadge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { CheckCircle, XCircle, Loader2, User, Calendar, Clock, Lock, Unlock, Pencil, ClipboardList, Users } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const generateMonthOptions = () => {
  const options = [];
  const now = new Date();
  for (let i = 0; i < 12; i++) {
    const date = subMonths(now, i);
    options.push({ value: format(date, 'yyyy-MM'), label: format(date, 'MMMM yyyy') });
  }
  return options;
};

const monthOptions = generateMonthOptions();

export default function ReviewPage() {
  const { user } = useAuth();
  const isManager = user?.roles?.includes('ADMIN') || user?.roles?.includes('MANAGER');

  // Tab 1: Pending Review
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reviewDialog, setReviewDialog] = useState({ open: false, submission: null, action: null });
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Tab 2: Employee Timesheets
  const [employees, setEmployees] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState('');
  const [selectedMonth, setSelectedMonth] = useState(format(new Date(), 'yyyy-MM'));
  const [employeeEntries, setEmployeeEntries] = useState([]);
  const [entriesLoading, setEntriesLoading] = useState(false);

  // Edit entry dialog
  const [editDialog, setEditDialog] = useState({ open: false, entry: null });
  const [editHours, setEditHours] = useState('');
  const [editNotes, setEditNotes] = useState('');
  const [editSaving, setEditSaving] = useState(false);

  // Month lock
  const [lockedMonths, setLockedMonths] = useState([]);
  const [lockLoading, setLockLoading] = useState(false);
  const [lockDialog, setLockDialog] = useState(false);
  const [lockNotes, setLockNotes] = useState('');

  const isSelectedMonthLocked = lockedMonths.some((l) => l.year_month === selectedMonth);

  useEffect(() => {
    fetchSubmissions();
    if (isManager) {
      fetchLockedMonths();
      fetchEmployees();
    }
  }, [isManager]);

  const fetchEmployeeEntries = useCallback(async () => {
    if (!selectedEmployee) return;
    setEntriesLoading(true);
    try {
      const [year, month] = selectedMonth.split('-');
      const start = format(startOfMonth(new Date(parseInt(year), parseInt(month) - 1, 1)), 'yyyy-MM-dd');
      const end = format(endOfMonth(new Date(parseInt(year), parseInt(month) - 1, 1)), 'yyyy-MM-dd');
      const res = await axios.get(`${API_URL}/api/timesheet/entries`, {
        params: { start_date: start, end_date: end, target_user_id: selectedEmployee },
      });
      setEmployeeEntries(res.data);
    } catch {
      toast.error('Failed to load entries');
    } finally {
      setEntriesLoading(false);
    }
  }, [selectedEmployee, selectedMonth]);

  useEffect(() => {
    fetchEmployeeEntries();
  }, [fetchEmployeeEntries]);

  const fetchSubmissions = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/timesheet/submissions/pending`);
      setSubmissions(res.data);
    } catch {
      toast.error('Failed to load submissions');
    } finally {
      setLoading(false);
    }
  };

  const fetchLockedMonths = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/month-locks`);
      setLockedMonths(res.data);
    } catch {}
  };

  const fetchEmployees = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/users`);
      setEmployees(res.data);
    } catch {}
  };

  const handleLockMonth = async () => {
    setLockLoading(true);
    try {
      await axios.post(`${API_URL}/api/month-locks`, { year_month: selectedMonth, notes: lockNotes });
      setLockNotes('');
      setLockDialog(false);
      toast.success(`${format(new Date(selectedMonth + '-01'), 'MMMM yyyy')} locked`);
      fetchLockedMonths();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to lock month');
    } finally {
      setLockLoading(false);
    }
  };

  const handleUnlockMonth = async () => {
    try {
      await axios.delete(`${API_URL}/api/month-locks/${selectedMonth}`);
      toast.success(`${format(new Date(selectedMonth + '-01'), 'MMMM yyyy')} unlocked`);
      fetchLockedMonths();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to unlock month');
    }
  };

  const openEditDialog = (entry) => {
    setEditDialog({ open: true, entry });
    setEditHours(entry.hours.toString());
    setEditNotes(entry.notes || '');
  };

  const handleSaveEdit = async () => {
    if (!editDialog.entry) return;
    setEditSaving(true);
    try {
      await axios.put(`${API_URL}/api/timesheet/entries/${editDialog.entry.id}/manager`, {
        hours: parseFloat(editHours),
        notes: editNotes,
      });
      toast.success('Entry updated');
      setEditDialog({ open: false, entry: null });
      fetchEmployeeEntries();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update entry');
    } finally {
      setEditSaving(false);
    }
  };

  const openReviewDialog = (submission, action) => {
    setReviewDialog({ open: true, submission, action });
    setComment('');
  };

  const handleReview = async () => {
    if (!reviewDialog.submission || !reviewDialog.action) return;
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/timesheet/submissions/${reviewDialog.submission.id}/review`, {
        action: reviewDialog.action,
        comment,
      });
      toast.success(`Timesheet ${reviewDialog.action === 'approve' ? 'approved' : 'rejected'}`);
      setReviewDialog({ open: false, submission: null, action: null });
      setComment('');
      fetchSubmissions();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to review submission');
    } finally {
      setSubmitting(false);
    }
  };

  const groupEntriesByProject = (entries) => {
    const grouped = {};
    entries.forEach((entry) => {
      if (!grouped[entry.project_id]) {
        grouped[entry.project_id] = {
          project_name: entry.project_name,
          project_code: entry.project_code,
          entries: [],
          total: 0,
        };
      }
      grouped[entry.project_id].entries.push(entry);
      grouped[entry.project_id].total += entry.hours;
    });
    return Object.values(grouped);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Review</h1>
        <p className="text-muted-foreground">Review submitted timesheets and manage employee entries</p>
      </div>

      <Tabs defaultValue="pending" className="space-y-4">
        <TabsList>
          <TabsTrigger value="pending" className="gap-2" data-testid="pending-tab">
            <ClipboardList className="w-4 h-4" />
            Pending Review
            {submissions.length > 0 && (
              <Badge className="ml-1 h-5 px-1.5 min-w-[20px] bg-primary text-primary-foreground text-xs">
                {submissions.length}
              </Badge>
            )}
          </TabsTrigger>
          {isManager && (
            <TabsTrigger value="timesheets" className="gap-2" data-testid="timesheets-tab">
              <Users className="w-4 h-4" />
              Employee Timesheets
            </TabsTrigger>
          )}
        </TabsList>

        {/* TAB 1: Pending Review */}
        <TabsContent value="pending" className="space-y-4">
          {submissions.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <CheckCircle className="w-12 h-12 mx-auto text-emerald-500 mb-4" />
                <h3 className="text-lg font-medium">All caught up!</h3>
                <p className="text-muted-foreground mt-1">No timesheets pending review</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {submissions.map((submission) => {
                const projectGroups = groupEntriesByProject(submission.entries);
                return (
                  <Card key={submission.id} data-testid={`submission-card-${submission.id}`}>
                    <CardHeader>
                      <div className="flex items-start justify-between">
                        <div className="space-y-1">
                          <div className="flex items-center gap-2">
                            <User className="w-4 h-4 text-muted-foreground" />
                            <CardTitle className="text-lg">{submission.user_name}</CardTitle>
                          </div>
                          <CardDescription>{submission.user_email}</CardDescription>
                        </div>
                        <SubmissionStatusBadge status={submission.status} />
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="flex flex-wrap gap-4 text-sm">
                        <div className="flex items-center gap-2">
                          <Calendar className="w-4 h-4 text-muted-foreground" />
                          <span>{submission.period_start} to {submission.period_end}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Clock className="w-4 h-4 text-muted-foreground" />
                          <span className="font-medium">{submission.total_hours}h total</span>
                        </div>
                      </div>
                      <Accordion type="single" collapsible>
                        <AccordionItem value="details">
                          <AccordionTrigger>View Details</AccordionTrigger>
                          <AccordionContent>
                            <div className="space-y-4">
                              {projectGroups.map((group) => (
                                <div key={group.project_code} className="space-y-2">
                                  <div className="flex items-center justify-between">
                                    <h4 className="font-medium">{group.project_name}</h4>
                                    <span className="text-sm text-muted-foreground">{group.total}h</span>
                                  </div>
                                  <Table>
                                    <TableHeader>
                                      <TableRow>
                                        <TableHead>Date</TableHead>
                                        <TableHead className="text-right">Hours</TableHead>
                                        <TableHead>Notes</TableHead>
                                      </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                      {group.entries.map((entry) => (
                                        <TableRow key={entry.id}>
                                          <TableCell>{format(new Date(entry.work_date + 'T00:00:00'), 'EEE, MMM d')}</TableCell>
                                          <TableCell className="text-right font-medium">
                                            {entry.hours}h
                                            {entry.task_hours && entry.task_hours.length > 0 && (
                                              <div className="mt-1 space-y-0.5">
                                                {entry.task_hours.map((th) => (
                                                  <div key={th.task_id} className="text-xs text-muted-foreground text-right">
                                                    {th.task_name || th.task_id}: {th.hours}h
                                                  </div>
                                                ))}
                                              </div>
                                            )}
                                          </TableCell>
                                          <TableCell className="text-muted-foreground">{entry.notes || '-'}</TableCell>
                                        </TableRow>
                                      ))}
                                    </TableBody>
                                  </Table>
                                </div>
                              ))}
                            </div>
                          </AccordionContent>
                        </AccordionItem>
                      </Accordion>
                      <div className="flex justify-end gap-2 pt-4 border-t border-border">
                        <Button
                          variant="outline"
                          onClick={() => openReviewDialog(submission, 'reject')}
                          data-testid={`reject-btn-${submission.id}`}
                        >
                          <XCircle className="w-4 h-4 mr-2" />
                          Reject
                        </Button>
                        <Button
                          onClick={() => openReviewDialog(submission, 'approve')}
                          data-testid={`approve-btn-${submission.id}`}
                        >
                          <CheckCircle className="w-4 h-4 mr-2" />
                          Approve
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>

        {/* TAB 2: Employee Timesheets */}
        {isManager && (
          <TabsContent value="timesheets" className="space-y-4">
            {/* Controls */}
            <Card>
              <CardContent className="pt-6">
                <div className="flex flex-wrap items-end gap-4">
                  <div className="space-y-1.5 min-w-[220px]">
                    <label className="text-sm font-medium">Employee</label>
                    <Select value={selectedEmployee} onValueChange={setSelectedEmployee}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select employee…" />
                      </SelectTrigger>
                      <SelectContent>
                        {employees.map((emp) => (
                          <SelectItem key={emp.id} value={emp.id}>
                            {emp.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-1.5 min-w-[180px]">
                    <label className="text-sm font-medium">Month</label>
                    <Select value={selectedMonth} onValueChange={setSelectedMonth}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {monthOptions.map((opt) => (
                          <SelectItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="flex items-center gap-2 ml-auto">
                    {isSelectedMonthLocked ? (
                      <>
                        <div className="flex items-center gap-1.5 text-sm text-amber-600 bg-amber-50 dark:bg-amber-950/30 dark:text-amber-400 px-3 py-2 rounded-md border border-amber-200 dark:border-amber-800">
                          <Lock className="w-3.5 h-3.5" />
                          {format(new Date(selectedMonth + '-01'), 'MMMM yyyy')} is locked
                        </div>
                        <Button variant="outline" size="sm" onClick={handleUnlockMonth}>
                          <Unlock className="w-4 h-4 mr-1.5" />
                          Unlock
                        </Button>
                      </>
                    ) : (
                      <Button variant="outline" onClick={() => setLockDialog(true)}>
                        <Lock className="w-4 h-4 mr-2" />
                        Lock {format(new Date(selectedMonth + '-01'), 'MMMM yyyy')}
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Entries */}
            {!selectedEmployee ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <Users className="w-10 h-10 mx-auto text-muted-foreground mb-3" />
                  <p className="text-muted-foreground">Select an employee to view their timesheet</p>
                </CardContent>
              </Card>
            ) : entriesLoading ? (
              <div className="flex items-center justify-center h-32">
                <Loader2 className="w-6 h-6 animate-spin text-primary" />
              </div>
            ) : employeeEntries.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <p className="text-muted-foreground">
                    No entries for {format(new Date(selectedMonth + '-01'), 'MMMM yyyy')}
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {groupEntriesByProject(employeeEntries).map((group) => (
                  <Card key={group.project_code}>
                    <CardHeader className="pb-2">
                      <div className="flex items-center justify-between">
                        <div>
                          <CardTitle className="text-base">{group.project_name}</CardTitle>
                          <p className="text-sm text-muted-foreground">{group.project_code}</p>
                        </div>
                        <span className="text-sm font-medium">{group.total.toFixed(1)}h total</span>
                      </div>
                    </CardHeader>
                    <CardContent className="p-0">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Date</TableHead>
                            <TableHead className="text-right">Hours</TableHead>
                            <TableHead>Notes</TableHead>
                            <TableHead>Status</TableHead>
                            <TableHead className="w-12"></TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {group.entries.map((entry) => (
                            <TableRow key={entry.id}>
                              <TableCell>{format(new Date(entry.work_date + 'T00:00:00'), 'EEE, MMM d')}</TableCell>
                              <TableCell className="text-right font-medium">
                                {entry.hours}h
                                {entry.task_hours && entry.task_hours.length > 0 && (
                                  <div className="mt-1 space-y-0.5">
                                    {entry.task_hours.map((th) => (
                                      <div key={th.task_id} className="text-xs text-muted-foreground text-right">
                                        {th.task_name || th.task_id}: {th.hours}h
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </TableCell>
                              <TableCell className="text-muted-foreground max-w-[200px] truncate">
                                {entry.notes || '-'}
                              </TableCell>
                              <TableCell>
                                <SubmissionStatusBadge status={entry.status} />
                              </TableCell>
                              <TableCell>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => openEditDialog(entry)}
                                  title="Edit entry"
                                >
                                  <Pencil className="w-3.5 h-3.5" />
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        )}
      </Tabs>

      {/* Review Dialog */}
      <Dialog
        open={reviewDialog.open}
        onOpenChange={(open) => !open && setReviewDialog({ open: false, submission: null, action: null })}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {reviewDialog.action === 'approve' ? 'Approve Timesheet' : 'Reject Timesheet'}
            </DialogTitle>
            <DialogDescription>
              {reviewDialog.action === 'approve'
                ? 'This will approve the timesheet and lock the entries.'
                : 'This will reject the timesheet and allow the employee to make changes.'}
            </DialogDescription>
          </DialogHeader>
          <Textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder={
              reviewDialog.action === 'reject'
                ? 'Please provide a reason for rejection…'
                : 'Add a comment (optional)…'
            }
            rows={3}
            data-testid="review-comment"
          />
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setReviewDialog({ open: false, submission: null, action: null })}
            >
              Cancel
            </Button>
            <Button
              variant={reviewDialog.action === 'approve' ? 'default' : 'destructive'}
              onClick={handleReview}
              disabled={submitting || (reviewDialog.action === 'reject' && !comment.trim())}
              data-testid="confirm-review-btn"
            >
              {submitting ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Processing…</>
              ) : reviewDialog.action === 'approve' ? 'Approve' : 'Reject'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Entry Dialog */}
      <Dialog
        open={editDialog.open}
        onOpenChange={(open) => !open && setEditDialog({ open: false, entry: null })}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Timesheet Entry</DialogTitle>
            <DialogDescription>
              {editDialog.entry &&
                `${format(new Date(editDialog.entry.work_date + 'T00:00:00'), 'EEEE, MMMM d, yyyy')} — ${editDialog.entry.project_name}`}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <label className="text-sm font-medium">Hours</label>
              <Input
                type="number"
                min="0"
                max="24"
                step="0.5"
                value={editHours}
                onChange={(e) => setEditHours(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium">Notes</label>
              <Textarea
                value={editNotes}
                onChange={(e) => setEditNotes(e.target.value)}
                placeholder="Add notes…"
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialog({ open: false, entry: null })}>
              Cancel
            </Button>
            <Button onClick={handleSaveEdit} disabled={editSaving || !editHours}>
              {editSaving ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving…</>
              ) : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Lock Month Dialog */}
      <Dialog open={lockDialog} onOpenChange={setLockDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Lock {format(new Date(selectedMonth + '-01'), 'MMMM yyyy')}</DialogTitle>
            <DialogDescription>
              Once locked, no employee can add, edit, or delete entries for this month.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5">
            <label className="text-sm font-medium">Reason (optional)</label>
            <Input
              value={lockNotes}
              onChange={(e) => setLockNotes(e.target.value)}
              placeholder="e.g. Month-end close"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setLockDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleLockMonth} disabled={lockLoading}>
              {lockLoading ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Locking…</>
              ) : (
                <><Lock className="w-4 h-4 mr-2" />Lock Month</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
